import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import { usePreferredMeasureUnit } from "@/hooks/usePreferredMeasureUnit";
import { canChooseLanguage } from "@/lib/productLanguage";
import type { SupportedLanguage } from "@/i18n";
import type { MeasureUnit } from "@/lib/measurementConverter";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Beaker, CakeSlice, Check, ChefHat, Clock, CupSoda, Leaf, Milk, Salad, Scale,
  Stethoscope, Wheat, Zap, type LucideIcon } from "lucide-react";
import { Botao, Campo, Escolha, Marcacao } from "@/components/sistema";
import type { ItemMarcacao, OpcaoEscolha } from "@/components/sistema";

const TOUR_COMPLETED_PREFIX = "onboarding_tour_completed_v1_";

// Fix B: GlucoPhoto product IDs — tour skips /glucophoto step for non-owners
const GLUCOPHOTO_PRODUCT_IDS = ["7330458", "7468894", "7487694"];

interface SpotlightRect {
  x: number;
  y: number;
  w: number;
  h: number;
  rx: number;
}

interface TourStepDef {
  target: string;
  title: string;
  description: string;
  navigateTo?: string;
  scrollToTarget?: boolean;
}

// ─────────────────────────────────────────────────────────────
// QUIZ STEP DEFINITIONS
// ─────────────────────────────────────────────────────────────
interface QuizOption {
  value: string;
  /** Opcional: idioma não leva ícone, o nome do idioma já é o rótulo. */
  icone?: LucideIcon;
  labelKey: string;
}

interface QuizStep {
  id: "name" | "language" | "measure_unit" | "goal" | "restriction" | "cooking_time";
  type: "options" | "text";
  titleKey: string;
  subtitleKey: string;
  placeholderKey?: string;
  options?: QuizOption[];
  // Lets the user pick more than one option (e.g. gluten free + lactose
  // intolerant at once). Stored as a comma-joined string in `answers`,
  // same shape as every other step, so onComplete/onStepAnswered don't
  // need to change.
  multiSelect?: boolean;
}

const QUIZ_STEPS: QuizStep[] = [
  {
    id: "language",
    type: "options",
    titleKey: "onboarding.quiz_language_title",
    subtitleKey: "onboarding.quiz_language_subtitle",
    options: [
      { value: "en", labelKey: "onboarding.quiz_lang_en" },
      { value: "es", labelKey: "onboarding.quiz_lang_es" },
      { value: "de", labelKey: "onboarding.quiz_lang_de" },
    ],
  },
  {
    id: "name",
    type: "text",
    titleKey: "onboarding.quiz_name_title",
    subtitleKey: "onboarding.quiz_name_subtitle",
    placeholderKey: "onboarding.quiz_name_placeholder",
  },
  {
    id: "measure_unit",
    type: "options",
    titleKey: "onboarding.quiz_measure_title",
    subtitleKey: "onboarding.quiz_measure_subtitle",
    options: [
      { value: "metric", icone: Scale, labelKey: "onboarding.quiz_unit_metric" },
      { value: "cups", icone: CupSoda, labelKey: "onboarding.quiz_unit_cups" },
      { value: "ounces", icone: Beaker, labelKey: "onboarding.quiz_unit_ounces" },
    ],
  },
  {
    id: "goal",
    type: "options",
    titleKey: "onboarding.quiz_goal_title",
    subtitleKey: "onboarding.quiz_goal_subtitle",
    options: [
      { value: "enjoy_food", icone: CakeSlice, labelKey: "onboarding.quiz_goal_enjoy" },
      { value: "glucose",    icone: Stethoscope, labelKey: "onboarding.quiz_goal_glucose" },
      { value: "weight",     icone: Scale, labelKey: "onboarding.quiz_goal_weight" },
      { value: "healthy",    icone: Salad, labelKey: "onboarding.quiz_goal_healthy" },
    ],
  },
  {
    id: "restriction",
    type: "options",
    multiSelect: true,
    titleKey: "onboarding.quiz_restriction_title",
    subtitleKey: "onboarding.quiz_restriction_subtitle",
    options: [
      { value: "gluten_free",    icone: Wheat, labelKey: "onboarding.quiz_restriction_gluten" },
      { value: "lactose_free",   icone: Milk, labelKey: "onboarding.quiz_restriction_lactose" },
      { value: "vegetarian",     icone: Leaf, labelKey: "onboarding.quiz_restriction_vegetarian" },
      { value: "none",           icone: Check, labelKey: "onboarding.quiz_restriction_none" },
    ],
  },
  {
    id: "cooking_time",
    type: "options",
    titleKey: "onboarding.quiz_time_title",
    subtitleKey: "onboarding.quiz_time_subtitle",
    options: [
      { value: "15min",  icone: Zap, labelKey: "onboarding.quiz_time_15" },
      { value: "30min",  icone: Clock, labelKey: "onboarding.quiz_time_30" },
      { value: "60min",  icone: ChefHat, labelKey: "onboarding.quiz_time_60" },
    ],
  },
];

// Used when the user already completed the full quiz but never gave a name
// (e.g. onboarding_completed_at is set but display_name is still null).
const NAME_ONLY_STEPS: QuizStep[] = QUIZ_STEPS.filter((s) => s.id === "name");

// ─────────────────────────────────────────────────────────────
// QUIZ COMPONENT
// ─────────────────────────────────────────────────────────────
interface QuizProps {
  steps: QuizStep[];
  onComplete: (prefs: Record<string, string>) => void;
  onSkip: () => void;
  onStepAnswered?: (stepId: string, value: string) => void;
}

const OnboardingQuiz = ({ steps, onComplete, onSkip, onStepAnswered }: QuizProps) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<string | null>(null);
  const [selectedMulti, setSelectedMulti] = useState<string[]>([]);
  // Only for the overlay's one-time entrance — never touched again after mount,
  // so the overlay itself never goes back to opacity-0 between questions.
  const [fadeIn, setFadeIn] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setFadeIn(true));
  }, []);

  // O container inteiro rola, e a rolagem NÃO se reseta sozinha ao trocar de
  // pergunta: quem rolou até o fim da pergunta 3 para alcançar o Continuar
  // começa a pergunta 4 na mesma altura, vendo o rodapé da tela anterior no
  // topo. Foi exatamente assim que a cliente descreveu — "en la parte superior
  // se ve un poco de continuar de la pregunta anterior".
  const scrollRef = useRef<HTMLDivElement>(null);

  // Reset selected when step changes
  useEffect(() => {
    const stepDef = steps[step];
    const stored = answers[stepDef.id] ?? "";
    if (stepDef.multiSelect) {
      setSelectedMulti(stored ? stored.split(",") : []);
    } else {
      setSelected(stored || null);
    }
    scrollRef.current?.scrollTo({ top: 0 });
  }, [step]);

  const currentStep = steps[step];
  const isLast = step === steps.length - 1;
  const isTextStep = currentStep.type === "text";
  const canAdvance = isTextStep ? true : currentStep.multiSelect ? selectedMulti.length > 0 : !!selected;

  const handleSelect = (value: string) => {
    setSelected(value);
  };

  // "None" is mutually exclusive with every other option — value is always
  // the canonical "none" regardless of UI language, only the label is
  // translated (see quiz_restriction_none), so this check works in ES/DE too.
  const handleToggleMulti = (value: string) => {
    setSelectedMulti((prev) => {
      if (value === "none") {
        return prev.includes("none") ? [] : ["none"];
      }
      const withoutNone = prev.filter((v) => v !== "none");
      return withoutNone.includes(value)
        ? withoutNone.filter((v) => v !== value)
        : [...withoutNone, value];
    });
  };

  const handleNext = () => {
    if (!canAdvance) return;
    const value = currentStep.multiSelect ? selectedMulti.join(",") : (selected ?? "").trim();
    const newAnswers = { ...answers, [currentStep.id]: value };
    setAnswers(newAnswers);

    if (value) onStepAnswered?.(currentStep.id, value);

    if (isLast) {
      onComplete(newAnswers);
    } else {
      setStep((s) => s + 1);
    }
  };

  const OPCOES: OpcaoEscolha<string>[] = (currentStep.options ?? []).map((o) => ({
    valor: o.value,
    rotulo: t(o.labelKey),
    icone: o.icone,
  }));

  const MARCACOES: ItemMarcacao[] = (currentStep.options ?? []).map((o) => ({
    valor: o.value,
    rotulo: t(o.labelKey),
    icone: o.icone,
  }));

  return (
    <div
      ref={scrollRef}
      className={`fixed inset-0 z-[60] flex flex-col overflow-y-auto bg-background transition-opacity duration-300 ${fadeIn ? "opacity-100" : "opacity-0"}`}
    >
      {/* Progresso: só o fio. O "3 / 6" que ficava em cima da pergunta saiu —
          é a segunda informação de uma tela que só tem uma pergunta, e o dono
          não quer contador de passo em tela nenhuma. */}
      <div className="flex" style={{ gap: 6, padding: "32px var(--gutter) 0" }}>
        {steps.map((_, i) => (
          <div
            key={i}
            style={{
              height: 6,
              flex: 1,
              borderRadius: 999,
              background: i <= step ? "hsl(var(--accent))" : "hsl(var(--border))",
            }}
          />
        ))}
      </div>

      {/* Uma pergunta por tela, cada uma montada com a peça certa: escolha de
          uma, marcação de várias, ou campo de texto. */}
      <div key={step} className="flex-1" style={{ paddingTop: 28 }}>
        <h2
          className="font-bold leading-tight"
          style={{ padding: "0 var(--gutter)", fontSize: "var(--t-tela)" }}
        >
          {t(currentStep.titleKey)}
        </h2>
        <p
          className="leading-relaxed"
          style={{
            padding: "12px var(--gutter) 0",
            fontSize: "var(--t-corpo)",
            color: "var(--tinta-apoio)",
          }}
        >
          {t(currentStep.subtitleKey)}
        </p>

        <div style={{ paddingTop: 24 }}>
          {isTextStep ? (
            <Campo
              valor={selected ?? ""}
              onMudar={(v) => setSelected(v)}
              placeholder={currentStep.placeholderKey ? t(currentStep.placeholderKey) : undefined}
              autoFocus
            />
          ) : currentStep.multiSelect ? (
            <Marcacao itens={MARCACOES} marcados={selectedMulti} onAlternar={handleToggleMulti} />
          ) : (
            <Escolha opcoes={OPCOES} valor={selected ?? ""} onEscolher={handleSelect} />
          )}
        </div>
      </div>

      {/* Sticky: numa lista longa (restrições tem quatro opções) o Continuar
          caía abaixo da dobra e a pessoa ficava sem saída visível. Grudado no
          rodapé ele existe sempre, independente do tamanho da pergunta. */}
      <div
        className="sticky bottom-0 bg-background"
        style={{
          padding: "var(--entre-secoes) var(--gutter) 40px",
          display: "grid",
          gap: 12,
        }}
      >
        <Botao
          rotulo={isLast ? t("onboarding.quiz_start") : t("onboarding.quiz_next")}
          desabilitado={!canAdvance}
          onClick={handleNext}
        />
        <Botao
          rotulo={t("onboarding.skip", "Skip")}
          variante="secundaria"
          onClick={onSkip}
        />
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN ONBOARDING COMPONENT
// ─────────────────────────────────────────────────────────────
const OnboardingExperience = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, hasProduct } = useAuth();
  const { setLanguage } = useLocale();
  const { setUnit: setMeasureUnit } = usePreferredMeasureUnit();
  const userId = user?.id;
  const ownsGlucoPhoto = GLUCOPHOTO_PRODUCT_IDS.some((id) => hasProduct(id));

  // Perguntar o idioma só faz sentido para quem tem catálogo em mais de um.
  // Para todos os outros o idioma vem do produto, e a resposta do quiz seria
  // gravada como preferência — foi exatamente assim que 416 contas ficaram
  // com a interface num idioma e o conteúdo em outro.
  const passosDoQuiz = useMemo(
    () => (canChooseLanguage(user?.products ?? []) ? QUIZ_STEPS : QUIZ_STEPS.filter((p) => p.id !== "language")),
    [user?.products],
  );

  const tourCompletedKey = userId ? `${TOUR_COMPLETED_PREFIX}${userId}` : null;

  // Quiz state — fetched directly from DB, decoupled from auth flow.
  // "unknown"   = not fetched yet
  // "full"      = onboarding_completed_at is null → show the whole quiz (5 questions)
  // "name_only" = onboarding_completed_at is set but display_name is empty → show just the name question
  // "done"      = nothing left to ask
  // LOCAL SCREENSHOT/DEMO SHIM (VITE_DEMO_AUTH === "1", never set in the
  // shipped build): the first-run quiz is gated on an app_users row, so with
  // no Supabase reachable it would sit on top of every screen forever.
  const demoMode = import.meta.env.VITE_DEMO_AUTH === "1";
  const [quizStatus, setQuizStatus] = useState<"unknown" | "full" | "name_only" | "done">(
    demoMode ? "done" : "unknown"
  );

  useEffect(() => {
    if (demoMode) return;
    if (!userId) return;
    supabase
      .from("app_users")
      .select("onboarding_completed_at, display_name")
      .eq("id", userId)
      .single()
      .then(({ data }) => {
        const completedAt = (data as any)?.onboarding_completed_at;
        const displayName = (data as any)?.display_name;
        if (!completedAt) {
          setQuizStatus("full");
        } else if (!displayName) {
          setQuizStatus("name_only");
        } else {
          setQuizStatus("done");
        }
      });
  }, [userId, demoMode]);

  // Tour states (unchanged from original)
  const [isTourActive, setIsTourActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [spotlight, setSpotlight] = useState<SpotlightRect | null>(null);
  const [fadeIn, setFadeIn] = useState(false);
  const [cardVisible, setCardVisible] = useState(false);
  const [transitioning, setTransitioning] = useState(false);

  const tourStartingRef = useRef(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const padding = 10;

  const tourSteps: TourStepDef[] = [
    {
      target: "home",
      title: t("onboarding.step_home", "Welcome to Glucofy"),
      description: t("onboarding.step_home_desc", "Your central hub for recipes, content, and tools designed for a healthier life."),
      navigateTo: "/home",
    },
    {
      target: "glucophoto",
      title: t("onboarding.step_glucophoto", "GlucoPhoto"),
      description: t("onboarding.step_glucophoto_desc", "Take a photo of your meal and get an instant AI analysis of its glycemic impact, with improvement suggestions."),
      navigateTo: "/glucophoto",
    },
    {
      target: "recipes",
      title: t("onboarding.step_recipes", "Recipes"),
      description: t("onboarding.step_recipes_desc", "Discover hundreds of recipes organized by category, time and glycemic profile."),
      navigateTo: "/recipes",
    },
    {
      target: "recipes",
      title: t("onboarding.step_creator", "Recipe Creator"),
      description: t("onboarding.step_creator_desc", "Generate custom recipe ideas with AI — from ingredients you have or as a surprise."),
      navigateTo: "/recipes",
    },
    {
      target: "more",
      title: t("onboarding.step_more", "More"),
      description: t("onboarding.step_more_desc", "Access your profile, saved recipes, grocery list, support, feedback, and more."),
      navigateTo: "/more",
    },
  ].filter((s) => s.target !== "glucophoto" || ownsGlucoPhoto);

  // ── Central start function ──
  const startGuidedTour = useCallback(() => {
    if (tourStartingRef.current || isTourActive) return;
    tourStartingRef.current = true;

    setCardVisible(false);
    setSpotlight(null);

    setIsTourActive(true);
    setCurrentStep(0);
    setFadeIn(true);

    const firstStep = tourSteps[0];
    if (firstStep.navigateTo && location.pathname !== firstStep.navigateTo) {
      navigate(firstStep.navigateTo);
    }

    tourStartingRef.current = false;
  }, [isTourActive, location.pathname, navigate]);

  // ── Listen for manual tutorial trigger (from More page) ──
  useEffect(() => {
    const handler = () => startGuidedTour();
    window.addEventListener("start-app-tutorial", handler);
    return () => window.removeEventListener("start-app-tutorial", handler);
  }, [startGuidedTour]);

  // ── End tour ──
  const endGuidedTour = useCallback((completed: boolean) => {
    setFadeIn(false);
    if (completed && tourCompletedKey) {
      localStorage.setItem(tourCompletedKey, "true");
    }
    setTimeout(() => {
      setIsTourActive(false);
      setCurrentStep(0);
      setCardVisible(false);
      setSpotlight(null);
      if (location.pathname !== "/home") navigate("/home");
    }, 300);
  }, [tourCompletedKey, currentStep, location.pathname, navigate]);

  // ── Measure spotlight target ──
  const measureTarget = useCallback((targetId: string, shouldScroll?: boolean) => {
    const el = document.querySelector(`[data-onboarding="${targetId}"]`);
    if (!el) {
      setSpotlight(null);
      return;
    }
    if (shouldScroll) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    const rect = el.getBoundingClientRect();
    const rx = Math.min(rect.width, rect.height) * 0.25;
    setSpotlight({
      x: rect.left - padding,
      y: rect.top - padding,
      w: rect.width + padding * 2,
      h: rect.height + padding * 2,
      rx,
    });
  }, []);

  // ── Update spotlight when tour step changes ──
  useEffect(() => {
    if (!isTourActive) return;
    const step = tourSteps[currentStep];

    if (step.navigateTo && location.pathname !== step.navigateTo) {
      navigate(step.navigateTo);
      const timer = setTimeout(() => measureTarget(step.target, step.scrollToTarget), 800);
      const showCard = setTimeout(() => {
        setCardVisible(true);
        setTransitioning(false);
      }, 1000);
      return () => { clearTimeout(timer); clearTimeout(showCard); };
    }

    const measureTimer = setTimeout(() => measureTarget(step.target, step.scrollToTarget), 300);
    const showCard = setTimeout(() => {
      setCardVisible(true);
      setTransitioning(false);
    }, 500);

    const handleResize = () => measureTarget(step.target);
    window.addEventListener("resize", handleResize);
    return () => {
      clearTimeout(measureTimer);
      clearTimeout(showCard);
      window.removeEventListener("resize", handleResize);
    };
  }, [isTourActive, currentStep, measureTarget, location.pathname]);

  // ── Next step ──
  const nextStep = () => {
    if (transitioning) return;
    if (currentStep < tourSteps.length - 1) {
      setTransitioning(true);
      setCardVisible(false);
      setSpotlight(null);
      setTimeout(() => setCurrentStep((s) => s + 1), 250);
    } else {
      endGuidedTour(true);
    }
  };

  // ── Card positioning ──
  const getCardStyle = (): React.CSSProperties => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (!spotlight) return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
    const targetCenterY = spotlight.y + spotlight.h / 2;
    if (targetCenterY > vh * 0.5) {
      return { bottom: `${vh - spotlight.y + 16}px`, left: "50%", transform: "translateX(-50%)", width: "min(320px, calc(100vw - 32px))" };
    }
    return { top: `${spotlight.y + spotlight.h + 16}px`, left: "50%", transform: "translateX(-50%)", width: "min(320px, calc(100vw - 32px))" };
  };

  // ─────────────────────────────────────────────────────────────
  // RENDER DECISION
  // ─────────────────────────────────────────────────────────────

  // Quiz — full 5-question version when onboarding was never completed, or just
  // the name question when it was completed but display_name is still empty.
  if (quizStatus === "full" || quizStatus === "name_only") {
    return (
      <OnboardingQuiz
        steps={quizStatus === "full" ? passosDoQuiz : NAME_ONLY_STEPS}
        onStepAnswered={(stepId, value) => {
          // setLanguage always marks the choice as manual (app_language_user_selected
          // = "true"), which is what stops useAutoLocale from reverting it right back
          // for EN-only/DE-only product owners on the very next render.
          if (stepId === "language") {
            setLanguage(value as SupportedLanguage);
          }
        }}
        onComplete={async (prefs) => {
          const language = prefs.language as SupportedLanguage | undefined;
          const measureUnit = prefs.measure_unit as MeasureUnit | undefined;
          const name = prefs.name?.trim();

          const updatePayload: Record<string, any> = {};
          if (quizStatus === "full") {
            updatePayload.onboarding_completed_at = new Date().toISOString();
            updatePayload.onboarding_preferences = prefs;
            if (language) updatePayload.preferred_language = language;
            if (measureUnit) updatePayload.preferred_measure_unit = measureUnit;
          }
          if (name) updatePayload.display_name = name;

          if (Object.keys(updatePayload).length > 0) {
            const { error } = await supabase
              .from("app_users")
              // `as any`: o payload é montado condicionalmente (Record<string,
              // any>), e o tipo gerado do Supabase rejeita índice aberto. É o
              // mesmo `as any` que as outras telas usam no update — o formato
              // dos campos não mudou.
              .update(updatePayload as any)
              .eq("id", userId!);

            if (error) {
              console.error("[OnboardingExperience] Failed to save onboarding preferences:", error);
              toast.error(t("onboarding.quiz_save_error", "Failed to save preferences. Please try again."));
              return; // keep the quiz open so the user can retry
            }
          }

          if (measureUnit) setMeasureUnit(measureUnit);
          setQuizStatus("done");
        }}
        onSkip={async () => {
          // In name_only mode, onboarding_completed_at is already set — skipping
          // the name question means there's nothing left to persist.
          if (quizStatus === "full") {
            const { error } = await supabase
              .from("app_users")
              .update({ onboarding_completed_at: new Date().toISOString() } as any)
              .eq("id", userId!);

            if (error) {
              console.error("[OnboardingExperience] Failed to mark onboarding as skipped:", error);
              toast.error(t("onboarding.quiz_save_error", "Failed to save preferences. Please try again."));
              return;
            }
          }
          setQuizStatus("done");
        }}
      />
    );
  }

  // ═══ EXISTING TOUR (unchanged — reachable only via the manual "start-app-tutorial" trigger) ═══

  // Tour overlay
  if (!isTourActive) return null;

  const step = tourSteps[currentStep];
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  return (
    <div
      ref={overlayRef}
      className={`fixed inset-0 z-[60] transition-opacity duration-300 ${fadeIn ? "opacity-100" : "opacity-0"}`}
      onClick={nextStep}
    >
      <svg className="fixed inset-0 w-full h-full" style={{ zIndex: 0 }}>
        <defs>
          <mask id="spotlight-mask">
            <rect x="0" y="0" width={vw} height={vh} fill="white" />
            {spotlight && (
              <rect
                x={spotlight.x}
                y={spotlight.y}
                width={spotlight.w}
                height={spotlight.h}
                rx={spotlight.rx}
                ry={spotlight.rx}
                fill="black"
                style={{ transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)" }}
              />
            )}
          </mask>
        </defs>
        <rect
          x="0" y="0" width={vw} height={vh}
          fill="hsl(0 0% 0% / 0.6)"
          mask="url(#spotlight-mask)"
        />
      </svg>

      {/* Cartão do passo. Antes: fundo `hsl(35 40% 97%)` e bolinhas
          `hsl(30 20% 80%)` escritos na mão (no tema escuro, um cartão claro
          num app preto), título em 16px, texto em 14px, "Skip" em 12px e o
          avançar num alvo de ~32px. Agora sai tudo da régua, e as duas ações
          se empilham em 56px cada — a fileira de bolinhas saiu porque o fio
          de progresso já é o mesmo aviso, sem virar terceira informação. */}
      <div
        className="fixed z-10 cartao"
        style={{
          ...getCardStyle(),
          padding: 20,
          opacity: cardVisible ? 1 : 0,
          transform: `${cardVisible ? "translateX(-50%)" : "translateX(-50%) translateY(8px)"}`,
          transition: "opacity 0.3s ease, transform 0.3s ease",
          pointerEvents: cardVisible ? "auto" : "none",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-bold leading-tight" style={{ fontSize: "var(--t-item)" }}>
          {step.title}
        </h3>
        <p
          className="leading-relaxed"
          style={{ fontSize: "var(--t-corpo)", color: "var(--tinta-apoio)", paddingTop: 8 }}
        >
          {step.description}
        </p>

        <div style={{ paddingTop: 20, display: "grid", gap: 12 }}>
          <Botao
            rotulo={currentStep === tourSteps.length - 1 ? t("onboarding.finish") : t("onboarding.next")}
            onClick={nextStep}
          />
          <Botao
            rotulo={t("onboarding.skip", "Skip")}
            variante="secundaria"
            onClick={() => endGuidedTour(false)}
          />
        </div>
      </div>
    </div>
  );
};

export default OnboardingExperience;
